import type { TFunction } from 'i18next'
import type { ActivityEvent } from '@ousse/shared'

/** Message lisible d'une ligne du journal (clés i18n `activity.events.*`). */
export function formatActivityMessage(t: TFunction, event: ActivityEvent): string {
  const user = event.displayName?.trim() || t('activity.someone')
  const data = event.data
  const str = (key: string) => (typeof data[key] === 'string' ? (data[key] as string) : '')
  const name = str('name').trim() || t('inventory.untitledItem')
  const location = str('locationName').trim() || t('inventory.uncategorized')
  const from = str('fromLocationName').trim() || t('inventory.uncategorized')
  const to = str('toLocationName').trim() || t('inventory.uncategorized')
  const parent = str('parentName').trim() || t('inventory.breadcrumbRoot')
  const count = typeof data.count === 'number' ? data.count : 0

  switch (event.type) {
    case 'item.created':
      return t('activity.events.item.created', { user, name, location })
    case 'item.updated':
      return t('activity.events.item.updated', { user, name })
    case 'item.moved':
      return t('activity.events.item.moved', { user, name, from, to })
    case 'item.deleted':
      return t('activity.events.item.deleted', { user, name })
    case 'item.restored':
      return t('activity.events.item.restored', { user, name })
    case 'location.created':
      return t('activity.events.location.created', { user, name })
    case 'location.updated':
      return t('activity.events.location.updated', { user, name })
    case 'location.moved':
      return t('activity.events.location.moved', { user, name, parent })
    case 'location.deleted':
      return t('activity.events.location.deleted', { user, name })
    case 'location.restored':
      return t('activity.events.location.restored', { user, name })
    case 'location.batch_created':
      return t('activity.events.location.batch_created', { user, count, parent })
    case 'photo.created':
      return t('activity.events.photo.created', { user })
    case 'photo.deleted':
      return t('activity.events.photo.deleted', { user })
    case 'attachment.created':
      return t('activity.events.attachment.created', {
        user,
        name: str('fileName').trim() || t('inventory.attachmentsTitle'),
      })
    case 'attachment.deleted':
      return t('activity.events.attachment.deleted', {
        user,
        name: str('fileName').trim() || t('inventory.attachmentsTitle'),
      })
    case 'loan.lent':
      return t('activity.events.loan.lent', {
        user,
        name,
        borrower: str('borrower').trim() || t('activity.someone'),
      })
    case 'loan.returned':
      return t('activity.events.loan.returned', {
        user,
        name,
        borrower: str('borrower').trim() || t('activity.someone'),
      })
    case 'member.joined':
      return t('activity.events.member.joined', { user })
    case 'member.left':
      return t('activity.events.member.left', { user })
    case 'member.removed':
      return t('activity.events.member.removed', { user, name: str('name').trim() || t('activity.someone') })
    default:
      return t('activity.events.unknown', { user })
  }
}

export function activityLink(event: ActivityEvent): string | null {
  if (event.entityType === 'item' && event.entityId) return `/items/${event.entityId}`
  if (event.entityType === 'location' && event.entityId) {
    if (event.type === 'location.deleted') return '/trash'
    return `/storage?locationId=${event.entityId}`
  }
  if (event.type.startsWith('photo.') && typeof event.data.itemId === 'string') {
    return `/items/${event.data.itemId}`
  }
  if (event.type.startsWith('photo.') && typeof event.data.locationId === 'string') {
    return `/storage?locationId=${event.data.locationId}`
  }
  if (event.type.startsWith('attachment.') && typeof event.data.itemId === 'string') {
    return `/items/${event.data.itemId}`
  }
  if (event.type.startsWith('member.')) return '/household'
  return null
}

/** Relative time courte (« il y a 5 min ») via Intl. */
export function formatRelativeTime(locale: string, iso: string, now = Date.now()): string {
  const diffSec = Math.round((new Date(iso).getTime() - now) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const abs = Math.abs(diffSec)
  if (abs < 60) return rtf.format(diffSec, 'second')
  const min = Math.round(diffSec / 60)
  if (Math.abs(min) < 60) return rtf.format(min, 'minute')
  const hour = Math.round(diffSec / 3600)
  if (Math.abs(hour) < 48) return rtf.format(hour, 'hour')
  const day = Math.round(diffSec / 86400)
  return rtf.format(day, 'day')
}
